import { EMPTY, Subject, fromEvent, timer } from 'rxjs';
import { filter, groupBy, map, mergeMap, pairwise, switchMap, take } from 'rxjs/operators';
import { Injectable, inject, DestroyRef, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ModalsService } from '@core/services/modals/modals.service';
import { isCloseOnBackHandle } from './modal-close-on-back.registry';

@Injectable({ providedIn: 'root' })
export class ModalBackHistoryService {
  private static readonly modalBackQueryParam = 'modalBack';
  private static readonly backActivationDelayMs = 1000;

  private readonly modalSelector = inject(ModalStoreSelector);
  private readonly modalsService = inject(ModalsService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly modalStates = toSignal(this.modalSelector.selectModals, {
    initialValue: [] as { handle: string; index?: number }[],
  });
  private readonly modalActivationEvents$ = new Subject<{
    handle: string;
    type: 'open' | 'close';
  }>();

  private readonly pushedEntries: { handle: string; token: string }[] = [];
  private pushedTokenIndex = 0;
  private closedByPopstate = false;

  private isNavigating = toSignal(
    this.router.events.pipe(
      filter(
        (event): event is NavigationEnd | NavigationStart =>
          event instanceof NavigationEnd || event instanceof NavigationStart,
      ),
      map(event => (event instanceof NavigationStart ? true : false)),
    ),
  );

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.stripModalBackQueryParamOnBootstrap();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.stripModalBackQueryParamOnBootstrap());

    this.modalActivationEvents$
      .pipe(
        groupBy(event => event.handle),
        mergeMap(group$ =>
          group$.pipe(
            switchMap(event =>
              event.type === 'open'
                ? timer(ModalBackHistoryService.backActivationDelayMs).pipe(
                    filter(() => {
                      const topModal = this.getTopModal(this.modalStates());
                      return (
                        !!topModal &&
                        topModal.handle === event.handle &&
                        isCloseOnBackHandle(event.handle)
                      );
                    }),
                    map(() => event.handle),
                  )
                : EMPTY,
            ),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(handle => this.pushModalBackQueryParam(handle));

    this.modalSelector.selectModals
      .pipe(pairwise(), takeUntilDestroyed(this.destroyRef))
      .subscribe(([prevModals, currModals]) => {
        const topModal = this.getTopModal(currModals);
        const prevTopModal = this.getTopModal(prevModals);

        if (
          topModal &&
          isCloseOnBackHandle(topModal.handle) &&
          prevTopModal?.handle !== topModal.handle
        ) {
          this.modalActivationEvents$.next({ handle: topModal.handle, type: 'open' });
        }

        const prevTopHandle = prevTopModal?.handle;
        if (
          prevTopHandle &&
          isCloseOnBackHandle(prevTopHandle) &&
          !currModals.some(m => m.handle === prevTopHandle)
        ) {
          this.modalActivationEvents$.next({ handle: prevTopHandle, type: 'close' });
          if (this.closedByPopstate) {
            this.closedByPopstate = false;
          } else if (
            this.pushedEntries.length > 0 &&
            this.pushedEntries[this.pushedEntries.length - 1].handle === prevTopHandle
          ) {
            this.pushedEntries.pop();
            // Defer so a concurrent navigation (e.g. to product-add) can complete first
            this.syncModalBackQueryParamWithTopEntry();
          }
        }
      });

    fromEvent<PopStateEvent>(window, 'popstate')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.pushedEntries.length > 0) {
          const { handle } = this.pushedEntries.pop()!;
          this.closedByPopstate = true;
          this.modalsService.close(handle);
        }
      });
  }

  private pushModalBackQueryParam(handle: string): void {
    this.pushedTokenIndex += 1;
    const token = `${handle}:${this.pushedTokenIndex}`;
    this.pushedEntries.push({ handle, token });
    void this.router.navigate([], {
      queryParams: { [ModalBackHistoryService.modalBackQueryParam]: token },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  private syncModalBackQueryParamWithTopEntry(): void {
    const topEntry = this.pushedEntries[this.pushedEntries.length - 1];
    // when their is navigation from the bogof modal to the product add page, this navigation will cancel the current navigation and block the navigation to the product add page
    // so we need to check if the navigation is still happening and if so, we need to wait for it to complete before navigating to the product add page
    if (this.isNavigating()) {
      return;
    }
    void this.router.navigate([], {
      queryParams: {
        [ModalBackHistoryService.modalBackQueryParam]: topEntry ? topEntry.token : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private stripModalBackQueryParamOnBootstrap(): void {
    const currentUrlTree = this.router.parseUrl(this.router.url);
    if (!(ModalBackHistoryService.modalBackQueryParam in currentUrlTree.queryParams)) {
      return;
    }

    delete currentUrlTree.queryParams[ModalBackHistoryService.modalBackQueryParam];
    void this.router.navigateByUrl(currentUrlTree, { replaceUrl: true });
  }

  private getTopModal(
    modals: { handle: string; index?: number }[],
  ): { handle: string; index?: number } | null {
    if (!modals.length) return null;
    return modals.reduce((a, b) => ((a.index ?? 0) > (b.index ?? 0) ? a : b));
  }
}
